import * as React from "react"

import { cn } from "@/lib/utils"

import {
  ACTION_LABEL,
  STAGE_LABEL,
  type AssignmentAction,
  type AssignmentEvent,
} from "../data/review-data"

/*
   The routing trail's own rendering.

   Split out of the sheet to keep that file nearer the 300-line limit: these
   turn one stored event into a row and decide nothing, so they move without
   taking any routing behaviour with them.
*/

const ACTION_DOT: Record<AssignmentAction, string> = {
  assign: "bg-info",
  reassign: "bg-info",
  throwback: "bg-text-3",
  advance_stage: "bg-master",
  skip_secondary: "bg-print",
  archive: "bg-text-2",
  unarchive: "bg-border-strong",
}

/**
 * One entry in the trail timeline.
 *
 * Reads the raw AssignmentEvent — action, from/to user, target stage,
 * note — and renders a single row. Every event is rich enough to answer
 * "what happened and why", not just "the stage moved".
 */
export function TrailEntry({
  event,
  isFirst,
  isLast,
}: {
  event: AssignmentEvent
  isFirst: boolean
  isLast: boolean
}): JSX.Element {
  const title = summarize(event)
  const dot = ACTION_DOT[event.action]
  return (
    <li className="grid grid-cols-[18px_minmax(0,1fr)] gap-3">
      <span className="relative flex justify-center">
        {!isFirst && <span aria-hidden="true" className="absolute top-0 h-[10px] w-px bg-border" />}
        <span
          aria-hidden="true"
          className={cn("mt-[10px] size-2.5 shrink-0 rounded-full ring-2 ring-surface", dot)}
        />
        {!isLast && (
          <span aria-hidden="true" className="absolute bottom-0 top-[22px] w-px bg-border" />
        )}
      </span>
      <div className={cn("min-w-0 pb-4", isLast && "pb-0")}>
        <p className="text-body-sm leading-snug text-text">
          <span className="font-medium">{title}</span>
          {event.actor && <span className="text-text-3"> · {event.actor}</span>}
          {/* The server records when a transition was made against the holder's
              position. On a regulatory trail that is the detail an auditor is
              looking for, so it is marked rather than left to be inferred. */}
          {event.positionOverride && <span className="text-print"> · override</span>}
        </p>
        {event.note && (
          <p className="mt-1 rounded-[8px] border-l-2 border-border bg-surface-sunken/50 px-2.5 py-1.5 text-caption leading-normal text-text-2">
            {event.note}
          </p>
        )}
        <p className="mt-1 font-id text-[10.5px] tabular-nums text-text-3">
          {formatTrailDate(event.at)}
        </p>
      </div>
    </li>
  )
}

/**
 * Build the trail entry's title line from the event shape. The backend
 * gives us structured data (action + fromUser + toUser + toStage) — using
 * it means the user reads "Reassigned Nadia → Marisol" instead of the
 * flat "reassigned" a lazy renderer would produce.
 */
function summarize(e: AssignmentEvent): string {
  switch (e.action) {
    case "assign":
      return e.toUser ? `${ACTION_LABEL.assign} to ${e.toUser}` : ACTION_LABEL.assign
    case "reassign":
      if (e.fromUser && e.toUser) return `Reassigned ${e.fromUser} → ${e.toUser}`
      if (e.toUser) return `Reassigned to ${e.toUser}`
      return ACTION_LABEL.reassign
    case "throwback":
      return e.fromUser ? `Returned to pool from ${e.fromUser}` : ACTION_LABEL.throwback
    case "advance_stage":
      return e.toStage ? `Advanced to ${STAGE_LABEL[e.toStage]}` : ACTION_LABEL.advance_stage
    case "skip_secondary":
      return ACTION_LABEL.skip_secondary
    case "archive":
      return ACTION_LABEL.archive
    case "unarchive":
      return ACTION_LABEL.unarchive
  }
}

function formatTrailDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

/** Live relative time — updates every minute while the sheet is open. */
export function RelativeTime({ iso }: { iso: string }): JSX.Element {
  const [, tick] = React.useReducer((n: number) => n + 1, 0)
  React.useEffect(() => {
    const t = setInterval(tick, 60_000)
    return () => {
      clearInterval(t)
    }
  }, [])
  const then = new Date(iso).getTime()
  const now = Date.now()
  const mins = Math.max(0, Math.round((now - then) / 60_000))
  if (mins < 1) return <>just now</>
  if (mins < 60) return <>{mins}m</>
  const hours = Math.round(mins / 60)
  if (hours < 24) return <>{hours}h</>
  return <>{Math.round(hours / 24)}d</>
}
